"use client"

import { BudgetHealth } from "@/features/budgeting/model/budget-enums"
import type { BudgetOverviewPayload } from "@/features/budgeting/model/budget-types"
import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { ProgressBar, type ProgressTone } from "@/features/finance/overview/components/progress-bar"
import { formatPercent, moneyUtilizationPercent } from "@/features/finance/overview/lib/overview-format"
import { useTranslations } from "@/i18n"

const MAX_ROWS = 3

/** Server-computed health to a bar tone. Never recomputed here. */
function toneFor(health: BudgetHealth): ProgressTone {
  if (health === BudgetHealth.Critical) return "destructive"
  if (health === BudgetHealth.Warning) return "warning"
  if (health === BudgetHealth.Healthy) return "success"
  return "muted"
}

/**
 * Budget (D4): how much of each active budget's planned spending is used, from the active-overview endpoint
 * (ADR-0011: the server aggregates; this tile only renders). Health comes from the server.
 */
export function BudgetTile({
  overview,
  budgetsLink,
  createBudget,
}: {
  overview: BudgetOverviewPayload
  budgetsLink: HomeTileLink
  createBudget: HomeTileLink | null
}) {
  const t = useTranslations("dashboard.home.budget")
  const rows = (overview.budgetSummaries ?? []).slice(0, MAX_ROWS)
  const attention = rows.find((row) => row.healthStatus === BudgetHealth.Critical || row.healthStatus === BudgetHealth.Warning)

  return (
    <HomeTile title={t("title")} subtitle={t("subtitle")} link={budgetsLink} testId='home-tile-budget'>
      {rows.length === 0 ? (
        <HomeTileEmpty message={t("empty")} action={createBudget} />
      ) : (
        <>
          <ul className='flex flex-col gap-2.5'>
            {rows.map((row) => {
              const used = moneyUtilizationPercent(row.actualExpenses, row.plannedExpenses, row.currencyCode)
              return (
                <li key={row.id ?? row.budgetId} data-testid='budget-row' className='flex flex-col gap-1'>
                  <span className='flex gap-2 text-[12.5px]'>
                    <span className='truncate text-muted-foreground'>{row.title}</span>
                    <span className='ml-auto font-bold tabular-nums'>{formatPercent(used)}</span>
                  </span>
                  <ProgressBar value={used} tone={toneFor(row.healthStatus)} aria-label={row.title} className='h-2' />
                </li>
              )
            })}
          </ul>
          {attention ? (
            <span className='mt-auto text-[11.5px] font-bold text-amber-700 dark:text-amber-400'>
              {t("attention", { title: attention.title })}
            </span>
          ) : null}
        </>
      )}
    </HomeTile>
  )
}
